import { earliestAsOfDay } from "$root/pages/PortfolioDetails/as-of/asOf";
import { unreachable } from "@mdotm/utils/runtime";

/**
 * The as-of side of the bulk export review dialogs, reports and composition templates (MDOT-25016).
 *
 * One date for the whole selection. A proposal has no past, so a picked date takes the place of the
 * proposal choice: every portfolio exports its current state at that date, and one whose inception is
 * not before it has no snapshot to take and is left out. Everything here is decided from the list row
 * alone; whether a snapshot exists is the backend's answer, given later.
 */

export type ApplyTo = "CURRENT" | "PROPOSAL" | "BOTH";

export type DownloadReportReviewState = {
	/** The proposal choice, in force only while no date is picked. */
	applyTo: ApplyTo;
	/** As-of date (`YYYY-MM-DD`) the export is taken at; `null` is today. */
	asOf: string | null;
};

/** What a list row has to offer; a row without an inception date is dated on trust and left to the backend to refuse. */
type WithInception = { uuid?: string; creationTime?: string };

/** The proposal choice that is in force: a picked date leaves only the current state to export. */
export function effectiveApplyTo(state: DownloadReportReviewState): ApplyTo {
	return state.asOf != null ? "CURRENT" : state.applyTo;
}

/**
 * Whether the portfolio can carry `asOf` at all. The day after its inception is the earliest it can be
 * read at, the same bound the picker applies; an unknown inception is left to the backend to refuse.
 */
export function canBeDated(investment: WithInception, asOf: string | null): boolean {
	if (asOf == null) {
		return true;
	}
	const earliest = earliestAsOfDay(investment.creationTime);
	return earliest == null || asOf >= earliest;
}

/**
 * The earliest inception day in the selection (`YYYY-MM-DD`), which is the picker's lower bound: a date
 * only part of the selection can serve stays pickable, and the rest is left out.
 */
export function earliestInception(investments: WithInception[]): string | undefined {
	let earliest: string | undefined;
	for (const { creationTime } of investments) {
		const day = creationTime?.slice(0, 10);
		if (day && (earliest == null || day < earliest)) {
			earliest = day;
		}
	}
	return earliest;
}

export type BulkReportPage<T> = {
	investment: T;
	choice: "current" | "enhance";
	/** The date the page is taken at; `undefined` is today. */
	asOf: string | undefined;
};

/**
 * The pages Confirm asks for, in the order the request sends them. A portfolio that did not exist on
 * the date is left out.
 */
export function planBulkReportPages<T extends WithInception>(
	selection: { available: T[]; proposalReady: T[] },
	state: DownloadReportReviewState,
): BulkReportPage<T>[] {
	const { asOf } = state;
	const pagesOf = (investment: T, choices: Array<"current" | "enhance">): BulkReportPage<T>[] =>
		canBeDated(investment, asOf) ? choices.map((choice) => ({ investment, choice, asOf: asOf ?? undefined })) : [];
	const applyTo = effectiveApplyTo(state);
	switch (applyTo) {
		case "CURRENT":
			return [...selection.available, ...selection.proposalReady].flatMap((x) => pagesOf(x, ["current"]));
		case "PROPOSAL":
			// A portfolio without a proposal still goes out, as its current state.
			return [
				...selection.available.flatMap((x) => pagesOf(x, ["current"])),
				...selection.proposalReady.flatMap((x) => pagesOf(x, ["enhance"])),
			];
		case "BOTH":
			return [
				...selection.available.flatMap((x) => pagesOf(x, ["current"])),
				...selection.proposalReady.flatMap((x) => pagesOf(x, ["current", "enhance"])),
			];
		default:
			return unreachable(applyTo);
	}
}

/**
 * What Confirm will do, in one sentence. Read from the plan rather than from the selection, so it
 * cannot promise something the same function is about to decide otherwise.
 *
 * @param formatDay renders a `YYYY-MM-DD` the way the dialog's other dates are rendered.
 * @param noun what one plan entry exports, singular.
 * @param undated how an undated plan says when it is taken.
 */
export function summariseBulkReportPlan<T extends WithInception>(
	plan: BulkReportPage<T>[],
	selection: { available: T[]; proposalReady: T[] },
	formatDay: (day: string) => string,
	noun = "report",
	undated = "as of today",
): string {
	if (plan.length === 0) {
		return "Nothing to export.";
	}
	const reports = plan.length === 1 ? `1 ${noun}` : `${plan.length} ${noun}s`;
	// Every page of a plan is taken at the same day.
	const day = plan[0].asOf;
	const head = `Confirm exports ${reports}, ${day == null ? undated : `as of ${formatDay(day)}`}.`;
	const covered = new Set(plan.map((page) => page.investment));
	const left = [...selection.available, ...selection.proposalReady].filter((x) => !covered.has(x)).length;
	if (left === 0) {
		return head;
	}
	return `${head} ${left === 1 ? "1 portfolio is" : `${left} portfolios are`} not included.`;
}
